/**
 * What a shopper is told about a limited drop-table entry before they spend anything, in one
 * place, in the store's own words (its currency and its nouns). A compliance disclosure, so
 * platform words (the rule is in `storefrontCopy/shared.ts`) rather than a store's copy, which the
 * box page's contents and the dashboard's theme preview both read from here. Kept out of
 * `dropTable.ts`, of which `@loot-os/react` carries a byte-identical copy.
 *
 * The rules being disclosed, and where each one is enforced:
 *
 *  - A limited entry has a counted supply (`isLimited`, `limitedQuantity`), so another shopper
 *    can withdraw the last unit first.
 *  - A withdrawal of an item nobody can supply is refused with 409
 *    `inventory_item_not_withdrawable`. The shopper keeps the item and sells it for its value
 *    through `POST /v1/me/inventory/{id}/sell`. Nothing is credited on its own.
 *  - A MARKETPLACE entry is bought from an outside seller at withdrawal. Every withdrawal pays
 *    the environment's destination cost; a MARKETPLACE line adds the seller's surcharge above
 *    its US price on top, and some sellers have no price for a destination at all (422
 *    `unsupported_shipping_destination`).
 *  - A cancelled marketplace order writes the shopper a voucher worth the item's value
 *    (`settleCancelledWithdrawalLines`). That is the one place the word voucher belongs.
 *
 * `docs/fulfillment/tenants.md` holds the same rules for a tenant reader; the inventory page's
 * line for a unit already held is `withdrawalIssueText` in `inventoryUnits.ts`.
 */
import type { Currency } from "./currency.js";
import { WITHDRAWAL_ISSUE_LABEL } from "./inventoryUnits.js";
import type { DropTableItem } from "./primitives.js";
import type { StorefrontTerminology } from "./storefrontTerminology.js";

/** The nouns the disclosure names. */
export type DisclosureTerms = Pick<StorefrontTerminology, "box" | "item" | "voucher">;

/** The badge on a limited entry, and the popover's heading: "Limited item". */
export function limitedItemLabel(terms: DisclosureTerms): string {
  return `Limited ${terms.item.singular}`;
}

/** True when a counted number of the entry can be won: every listing, and a fixed-stock item. */
export function isLimitedEntry(item: Pick<DropTableItem, "isLimited">): boolean {
  return item.isLimited === true;
}

/** True when the entry is bought from an outside seller rather than held in the catalog. */
export function isMarketplaceEntry(item: Pick<DropTableItem, "itemSource">): boolean {
  return item.itemSource === "MARKETPLACE";
}

/**
 * What the popover says for one entry. The first two lines hold for every limited entry; the
 * rest apply to an outside seller's item, which ships and cancels on different terms.
 */
export function limitedItemDisclosure(
  item: Pick<DropTableItem, "itemSource">,
  currency: Pick<Currency, "namePlural">,
  terms: DisclosureTerms,
): string[] {
  const noun = terms.item.singular;
  const lines = [
    `A counted number of this ${noun} can be won. Another shopper can withdraw the last one first.`,
    `If you cannot withdraw it, you keep it and can sell it for its value in ${currency.namePlural}.`,
  ];
  if (!isMarketplaceEntry(item)) return lines;
  return [
    ...lines,
    `Withdrawing this ${noun} buys it from an outside seller.`,
    "Shipping to an address outside the US costs more, by what the seller charges above its US price. Some sellers ship to the US only.",
    `You cannot cancel a withdrawal once you have placed it. If the store cancels it for you, the ${noun} becomes one ${terms.voucher.singular} worth its value.`,
  ];
}

/**
 * A badge on an entry's art that opens what it means: the badge's words, the popover's title and
 * its lines, shown whole, and how loud it is. The badge sits on the art rather than under the
 * name, so every entry in the table is the same height.
 */
export interface DropEntryNotice {
  /** What it is about, for a key and a test: `unshippable` before `limited`. */
  kind: "unshippable" | "limited";
  /** The badge's words: "3 left", "Limited card", "Can't ship". */
  label: string;
  /** The popover's title. */
  title: string;
  lines: readonly string[];
  tone: "warning" | "danger";
}

/**
 * What an entry's badges say, loudest first, or `[]` when there is nothing to report. One that
 * can no longer be shipped says so, in the inventory's words for the same thing, and that it can
 * be sold for its value (never that it pays its value). A limited one counts what is left when
 * the count is known, and opens the limited disclosure either way.
 */
export function dropEntryNotices(
  item: Pick<DropTableItem, "itemSource" | "isGemSubstitute" | "isLimited" | "limitedQuantity">,
  currency: Pick<Currency, "namePlural">,
  terms: DisclosureTerms,
): DropEntryNotice[] {
  const notices: DropEntryNotice[] = [];
  if (item.isGemSubstitute)
    notices.push({
      kind: "unshippable",
      label: WITHDRAWAL_ISSUE_LABEL,
      title: WITHDRAWAL_ISSUE_LABEL,
      lines: [
        `This ${terms.item.singular} can no longer be shipped. If you win it, you can sell it for its value in ${currency.namePlural}.`,
      ],
      tone: "danger",
    });
  if (isLimitedEntry(item)) {
    const title = limitedItemLabel(terms);
    notices.push({
      kind: "limited",
      label: item.limitedQuantity == null ? title : `${item.limitedQuantity} left`,
      title,
      lines: limitedItemDisclosure(item, currency, terms),
      tone: "warning",
    });
  }
  return notices;
}

/**
 * The notes under the whole table. At most two: what a limited entry means for the box, and
 * whether any entry can no longer be shipped at all. An entry from an outside seller says more
 * than a fixed-stock catalog entry, so the first note takes the stronger form when one exists.
 */
export function dropTableDisclosureNotes(
  items: readonly Pick<DropTableItem, "itemSource" | "isLimited" | "isGemSubstitute">[],
  terms: DisclosureTerms,
): string[] {
  const { box, item } = terms;
  const notes: string[] = [];
  // "Any item", never "an item": a tenant's noun may start with any sound.
  if (items.some(isMarketplaceEntry)) {
    notes.push(
      `Some ${item.plural} in this ${box.singular} come from outside sellers. Supply is limited, withdrawing to an address outside the US costs extra, and any ${item.singular} you cannot withdraw can be sold for its value.`,
    );
  } else if (items.some(isLimitedEntry)) {
    notes.push(
      `Some ${item.plural} in this ${box.singular} have a limited supply. Any ${item.singular} you cannot withdraw can be sold for its value.`,
    );
  }
  if (items.some((entry) => entry.isGemSubstitute)) {
    notes.push(
      `Some ${item.plural} in this ${box.singular} can no longer be shipped. Winning one gives you the ${item.singular}, and you can sell it for its value.`,
    );
  }
  return notes;
}
